import { Fragment, useState, useEffect, useRef } from 'react';
import { useJwt, useAuthHeaders } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { Button, Input, Tabs } from '../ds/index.js';
const REFRESH_INTERVAL = 60_000;

/** Mesures du filtre « Afficher » (détail relevé chaque minute avec la présence). */
type Measure = 'total' | 'available' | 'away' | 'busy' | 'focus' | 'meeting' | 'conversation';
type Breakdown = Record<Measure, number>;

interface MeasureDef {
  id: Measure;
  /** Libellé du filtre. */
  label: string;
  /** « Temps … » : titre de l'anneau et des colonnes. */
  time: string;
  /** « Personnes … » : titre du graphique des personnes par jour. */
  people: string;
  color: string;
}

const MEASURES: MeasureDef[] = [
  { id: 'total', label: 'Tout le temps', time: 'Temps passé', people: 'Présents', color: 'var(--color-primary)' },
  { id: 'available', label: 'Disponible', time: 'Temps disponible', people: 'Personnes disponibles', color: 'var(--color-status-online)' },
  { id: 'away', label: 'Absent', time: 'Temps absent', people: 'Personnes absentes à un moment', color: 'var(--color-status-away)' },
  { id: 'busy', label: 'Occupé', time: 'Temps occupé', people: 'Personnes en « Occupé »', color: 'var(--color-status-busy)' },
  { id: 'focus', label: 'Focus', time: 'Temps en focus', people: 'Personnes en focus', color: '#7F77DD' },
  { id: 'meeting', label: 'En réunion', time: 'Temps en réunion', people: 'Personnes en réunion', color: 'var(--color-primary)' },
  { id: 'conversation', label: 'En discussion', time: 'Temps en discussion', people: 'Personnes en discussion', color: 'var(--color-accent)' },
];
const MEASURE_BY_ID = Object.fromEntries(MEASURES.map((m) => [m.id, m])) as Record<Measure, MeasureDef>;
const STATUS_MEASURES: Measure[] = ['available', 'away', 'busy', 'focus'];

interface DayStats { present: number; minutes: number; peak: number; time: Breakdown; people: Breakdown }
interface StatsData {
  period: number;
  /** Premier jour relevé (le relevé a commencé avec le déploiement). */
  since: string | null;
  totalMembers: number;
  onlineNow: number;
  today: DayStats & { day: string; peakAt: string | null };
  average7: DayStats;
  days: Array<DayStats & { day: string }>;
  /** Total de la période par mesure ; `undetailed` = minutes relevées avant le détail par statut. */
  totals: Breakdown & { undetailed: number };
  /** Par mesure, [lundi … dimanche][0 … 23 h] : personnes en moyenne. */
  heatmaps: Record<Measure, number[][]>;
  members: Array<{
    userId: string; displayName: string; role: string; lastSeenAt: string | null;
    daysPresent: number; minutes: number; todayMinutes: number; time: Breakdown; today: Breakdown;
  }>;
  zones: Array<{ zone: string; count: number }>;
}

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function getTenantIdFromJwt(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.tid as string) ?? '';
  } catch { return ''; }
}

function formatDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });

}

// ── Graphiques en barres (CSS pur, une seule série, pas de légende) ─────────

/**
 * Barres verticales : valeur affichée seulement sur le maximum (étiquettes
 * sélectives), détail au survol (title), résumé texte pour les lecteurs d'écran.
 */
function BarChart({ data, height, summary, tickEvery = 1, barWidth, color }: {
  data: Array<{ label: string; value: number }>;
  height: number;
  summary: string;
  tickEvery?: number;
  barWidth?: number | undefined;
  color?: string | undefined;
}) {
  const max = Math.max(...data.map((d) => d.value), 1);
  const cols = `repeat(${data.length}, minmax(0, 1fr))`;
  return (
    <div>
      <div role="img" aria-label={summary} style={{ position: 'relative', height, display: 'grid', gridTemplateColumns: cols, columnGap: 2, alignItems: 'end', borderBottom: '1px solid var(--color-border-control)' }}>
        <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: '50%', borderTop: '1px dashed var(--color-border-default)' }} />
        {data.map((d, i) => (
          <div key={i} title={`${d.label} : ${d.value}`} style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
            {barWidth && (
              <span className="pl-num" style={{ fontSize: 12, fontWeight: 500, visibility: d.value === max && d.value > 0 ? 'visible' : 'hidden' }}>{d.value}</span>
            )}
            <div className="pl-bar" style={{ ...(color ? { background: color } : {}), width: barWidth ?? '100%', height: Math.max(2, Math.round((d.value / max) * (height - (barWidth ? 24 : 4)))) }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: cols, columnGap: 2, marginTop: 8 }}>
        {data.map((d, i) => (
          <span key={i} className="pl-axis-label" style={{ visibility: i % tickEvery === 0 ? 'visible' : 'hidden', whiteSpace: 'nowrap' }}>{d.label}</span>
        ))}
      </div>
    </div>
  );
}

/** Durée lisible : « 45 min », « 3 h 05 ». */
function formatMinutes(min: number): string {
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return `${h} h ${String(m).padStart(2, '0')}`;
}

function formatLastSeen(iso: string | null): string {
  if (!iso) return 'Jamais';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60_000;
  if (diff < 2) return 'En ce moment';
  if (diff < 60) return `Il y a ${Math.round(diff)} min`;
  if (diff < 24 * 60) return `Il y a ${Math.round(diff / 60)} h`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

/**
 * Anneau façon montre : progression de la journée par rapport à la moyenne
 * des 7 jours précédents (plein = autant que d'habitude ; au-delà, un second
 * tour plus clair).
 */
function Ring({ value, target, label, display, sub }: { value: number; target: number; label: string; display: string; sub: string }) {
  const ratio = target > 0 ? value / target : value > 0 ? 1 : 0;
  const r = 34, c = 2 * Math.PI * r;
  const first = Math.min(ratio, 1), second = Math.min(Math.max(ratio - 1, 0), 1);
  return (
    <div className="pl-panel" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg width="84" height="84" viewBox="0 0 84 84" aria-hidden="true" style={{ flex: 'none', transform: 'rotate(-90deg)' }}>
        <circle cx="42" cy="42" r={r} fill="none" stroke="var(--color-bg-raised)" strokeWidth="10" />
        <circle cx="42" cy="42" r={r} fill="none" stroke="var(--color-primary)" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={`${first * c} ${c}`} />
        {second > 0 && (
          <circle cx="42" cy="42" r={r} fill="none" stroke="var(--color-primary-text)" strokeWidth="10" strokeLinecap="round"
            strokeDasharray={`${second * c} ${c}`} opacity="0.8" />
        )}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span className="pl-muted" style={{ fontSize: 13 }}>{label}</span>
        <span className="pl-num" style={{ font: '800 28px/1.05 var(--font-display)' }}>{display}</span>
        <span className="pl-muted" style={{ fontSize: 12 }}>{sub}</span>
      </div>
    </div>
  );
}

function Metric({ value, label, online }: { value: number | string; label: string; online?: boolean }) {
  return (
    <div className="pl-panel" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span className="pl-num" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, font: '800 32px/1.05 var(--font-display)' }}>
        {value}
        {online && <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 'var(--radius-full)', background: 'var(--color-status-online)' }} />}
      </span>
      <span className="pl-muted" style={{ fontSize: 13 }}>{label}</span>
    </div>
  );
}

/** Carte de chaleur jour × heure (présents en moyenne), de 7 h à 21 h, comme un agenda. */
function Heatmap({ data, noun = 'présent', color = 'var(--color-primary)' }: { data: number[][]; noun?: string; color?: string }) {
  const hours = Array.from({ length: 15 }, (_, i) => i + 7);
  const max = Math.max(...data.flat(), 0.1);
  let best = { w: 0, h: 0, v: 0 };
  data.forEach((row, w) => row.forEach((v, h) => { if (v > best.v) best = { w, h, v }; }));
  return (
    <div>
      <div role="img" aria-label={best.v > 0 ? `Moment le plus fréquenté : ${WEEKDAYS[best.w]} vers ${best.h} h, ${best.v} ${noun}s en moyenne` : 'Pas encore de données'}
        style={{ display: 'grid', gridTemplateColumns: `36px repeat(${hours.length}, minmax(0, 1fr))`, gap: 3 }}>
        <span />
        {hours.map((h) => <span key={h} className="pl-axis-label" style={{ textAlign: 'center', whiteSpace: 'nowrap', visibility: h % 2 === 1 ? 'visible' : 'hidden' }}>{h}h</span>)}
        {data.map((row, w) => (
          <Fragment key={w}>
            <span className="pl-axis-label" style={{ alignSelf: 'center' }}>{WEEKDAYS[w]}</span>
            {hours.map((h) => {
              const v = row[h] ?? 0;
              return (
                <span key={`${w}-${h}`} title={`${WEEKDAYS[w]} ${h} h : ${v} ${noun}${v >= 2 ? 's' : ''} en moyenne`}
                  style={{ height: 22, borderRadius: 4, background: v > 0 ? color : 'var(--color-bg-raised)', opacity: v > 0 ? 0.2 + 0.8 * (v / max) : 1 }} />
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/** Barre empilée + légende : part de chaque segment dans le total. */
function StackedBar({ title, segments, total }: {
  title: string;
  segments: Array<{ label: string; value: number; color: string }>;
  total: number;
}) {
  const shown = segments.filter((seg) => seg.value > 0);
  const pct = (v: number) => (total > 0 ? Math.round((v / total) * 100) : 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
      <div role="img" aria-label={`${title} : ${shown.map((seg) => `${seg.label} ${pct(seg.value)} %`).join(', ') || 'pas encore de données'}`}
        style={{ display: 'flex', height: 14, borderRadius: 'var(--radius-full)', overflow: 'hidden', background: 'var(--color-bg-raised)', gap: shown.length > 1 ? 2 : 0 }}>
        {shown.map((seg) => (
          <span key={seg.label} title={`${seg.label} : ${formatMinutes(seg.value)} (${pct(seg.value)} %)`}
            style={{ flex: `${seg.value} 0 0`, minWidth: 3, background: seg.color }} />
        ))}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
        {segments.map((seg) => (
          <li key={seg.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: seg.color }} />
            <span>{seg.label}</span>
            <span className="pl-num pl-muted">{formatMinutes(seg.value)} · {pct(seg.value)} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function exportCsv(stats: StatsData) {
  const rows = [
    ['Membre', 'Rôle', `Jours présents (${stats.period} j)`, ...MEASURES.map((d) => `${d.time} (minutes)`), 'Dernière venue'],
    ...stats.members.map((m) => [
      m.displayName, m.role, String(m.daysPresent), ...MEASURES.map((d) => String(m.time[d.id])), m.lastSeenAt ?? '',
    ]),
  ];
  const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(';')).join('\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `activite-${stats.today.day}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * AdminStats — Tableau de bord (rafraîchi chaque minute). Données relevées
 * chaque minute par l'API depuis la présence sur le campus.
 */
export function AdminStats() {
  const jwt = useJwt();
  const tenantId = getTenantIdFromJwt(jwt);
  const authHeaders = useAuthHeaders(tenantId);

  const [period, setPeriod] = useState<'7' | '30' | '90'>('30');
  const [measure, setMeasure] = useState<Measure>('total');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function fetchStats() {
    if (!jwt || !tenantId) return;
    fetch(`${API_BASE}/admin/stats?days=${period}`, { headers: authHeaders })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => setStats(data as StatsData))
      .catch(() => { /* ignore */ })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchStats();
    intervalRef.current = setInterval(fetchStats, REFRESH_INTERVAL);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [jwt, tenantId, period]);

  const header = (
    <header style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 className="pl-page-title">Statistiques</h1>
        <p className="pl-page-sub">
          {loading ? 'Chargement…' : !stats ? 'Impossible de charger les statistiques. Réessaie dans un instant.'
            : stats.since ? `Présence relevée chaque minute depuis le ${new Date(`${stats.since}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}.`
            : 'Le relevé de présence vient de commencer : les premiers chiffres arrivent dans une minute.'}
        </p>
      </div>
      <Tabs aria-label="Période" items={[{ id: '7', label: '7 jours' }, { id: '30', label: '30 jours' }, { id: '90', label: '90 jours' }]} value={period} onChange={(id) => setPeriod(id)} />
    </header>
  );
  if (loading || !stats) return header;

  const def = MEASURE_BY_ID[measure];
  const isTotal = measure === 'total';
  const tickEvery = stats.days.length > 31 ? 14 : stats.days.length > 7 ? 5 : 1;
  const presentData = stats.days.map((d) => ({ label: formatDay(d.day), value: isTotal ? d.present : d.people[measure] }));
  const minutesData = stats.days.map((d) => ({ label: formatDay(d.day), value: Math.round((isTotal ? d.minutes : d.time[measure]) / 60 * 10) / 10 }));
  const todayTime = isTotal ? stats.today.minutes : stats.today.time[measure];
  const todayPeople = isTotal ? stats.today.present : stats.today.people[measure];
  const avgTime = isTotal ? stats.average7.minutes : stats.average7.time[measure];
  const avgPerPerson = todayPeople > 0 ? todayTime / todayPeople : 0;
  const activeMembers = stats.members.filter((m) => m.daysPresent > 0).length;
  const t = stats.totals;
  const solo = Math.max(0, t.total - t.meeting - t.conversation);
  const query = search.trim().toLocaleLowerCase('fr');
  const memberRows = stats.members
    .filter((m) => !query || m.displayName.toLocaleLowerCase('fr').includes(query))
    .slice()
    .sort((a, b) => (isTotal ? 0 : b.time[measure] - a.time[measure]));

  return (
    <>
      {header}
      <div role="radiogroup" aria-label="Afficher" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className="pl-muted" style={{ fontSize: 13 }}>Afficher</span>
        <div className="pl-choices">
          {MEASURES.map((m, i) => (
            <Fragment key={m.id}>
              {(i === 1 || i === 5) && <span aria-hidden="true" style={{ width: 1, alignSelf: 'stretch', background: 'var(--color-border-default)', margin: '0 4px' }} />}
              <button type="button" role="radio" aria-checked={measure === m.id} className="pl-choice" onClick={() => setMeasure(m.id)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {m.id !== 'total' && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 'var(--radius-full)', background: m.color }} />}
                {m.label}
              </button>
            </Fragment>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
        <Ring label="Présents aujourd'hui" value={stats.today.present} target={stats.average7.present}
          display={`${stats.today.present} / ${stats.totalMembers}`} sub={`Moyenne 7 jours : ${stats.average7.present}`} />
        <Ring label={`${def.time} aujourd'hui`} value={todayTime} target={avgTime}
          display={formatMinutes(todayTime)} sub={avgPerPerson > 0 ? `${formatMinutes(avgPerPerson)} par personne concernée` : `Moyenne 7 jours : ${formatMinutes(avgTime)}`} />
        <Ring label="Pic du jour" value={stats.today.peak} target={stats.average7.peak}
          display={`${stats.today.peak}`}
          sub={stats.today.peakAt ? `atteint à ${new Date(stats.today.peakAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : `Moyenne 7 jours : ${stats.average7.peak}`} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
        <Metric value={stats.onlineNow} label="En ligne maintenant" online />
        <Metric value={stats.totalMembers} label="Membres" />
        <Metric value={activeMembers} label={`Venus au moins une fois (${stats.period} j)`} />
      </div>

      <section className="pl-panel" aria-label="Répartition du temps" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 className="pl-card-title">Répartition du temps ({stats.period} jours)</h2>
          <p className="pl-page-sub" style={{ margin: 0 }}>
            {formatMinutes(t.total)} passées sur le campus, tout le monde compris.
            {t.undetailed > 0 && ` Le détail par statut a commencé après les premiers relevés : ${formatMinutes(t.undetailed)} ne sont pas détaillées.`}
          </p>
        </div>
        <StackedBar title="Par statut" total={t.total} segments={[
          ...STATUS_MEASURES.map((m) => ({ label: MEASURE_BY_ID[m].label, value: t[m], color: MEASURE_BY_ID[m].color })),
          ...(t.undetailed > 0 ? [{ label: 'Non détaillé', value: t.undetailed, color: 'var(--color-status-offline)' }] : []),
        ]} />
        <StackedBar title="Par activité" total={t.total} segments={[
          { label: 'En réunion', value: t.meeting, color: MEASURE_BY_ID.meeting.color },
          { label: 'En discussion', value: t.conversation, color: MEASURE_BY_ID.conversation.color },
          { label: 'Hors discussion', value: solo, color: 'var(--color-status-offline)' },
        ]} />
        <p className="pl-muted" style={{ margin: 0, fontSize: 12 }}>
          En réunion : dans une salle de réunion avec au moins une autre personne. En discussion : dans une bulle ou un bureau avec au moins une autre personne.
        </p>
      </section>

      <section className="pl-panel" aria-label={`${def.people} par jour`} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h2 className="pl-card-title">{def.people} par jour</h2>
        <BarChart data={presentData} height={160} tickEvery={tickEvery} barWidth={stats.days.length <= 7 ? 36 : undefined} color={def.color}
          summary={`${def.people} par jour : ${presentData.map((d) => `${d.label} ${d.value}`).join(', ')}`} />
      </section>

      <section className="pl-panel" aria-label={`${def.time} par jour`} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h2 className="pl-card-title">{isTotal ? 'Temps passé sur le campus' : def.time} par jour (heures, tout le monde)</h2>
        <BarChart data={minutesData} height={140} tickEvery={tickEvery} barWidth={stats.days.length <= 7 ? 36 : undefined} color={def.color}
          summary={`Heures par jour : ${minutesData.map((d) => `${d.label} ${d.value}`).join(', ')}`} />
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 16 }}>
        <section className="pl-panel" aria-label="Heures et jours forts" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2 className="pl-card-title">Heures et jours forts{isTotal ? '' : ` - ${def.label.toLocaleLowerCase('fr')}`}</h2>
          <Heatmap data={stats.heatmaps[measure]} color={def.color} noun={isTotal ? 'présent' : 'personne'} />
        </section>
        <section className="pl-panel" aria-label="Zones occupées maintenant" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2 className="pl-card-title">Zones occupées maintenant</h2>
          {stats.zones.length === 0 ? (
            <p className="pl-page-sub" style={{ color: 'var(--color-text-muted)' }}>Personne sur le campus en ce moment.</p>
          ) : (
            <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {stats.zones.map((z, i) => (
                <li key={z.zone} style={{ display: 'flex', alignItems: 'center', gap: 12, height: 36, padding: '0 12px', borderRadius: 'var(--radius-md)', background: i === 0 ? 'var(--color-bg-raised)' : 'transparent' }}>
                  <span className="pl-num pl-muted" style={{ font: '700 11px/1 var(--font-mono)' }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 13 }}>{z.zone}</span>
                  <span className="pl-num pl-muted" style={{ fontSize: 12 }}>{z.count} {z.count > 1 ? 'personnes' : 'personne'}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      <section className="pl-panel" aria-label="Activité par membre" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 20px', flexWrap: 'wrap' }}>
          <h2 className="pl-card-title">Activité par membre ({stats.period} jours)</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 220 }}>
              <Input aria-label="Chercher un membre" placeholder="Chercher un membre" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Button variant="secondary" size="sm" icon="download" onClick={() => exportCsv(stats)}>Exporter (CSV)</Button>
          </div>
        </div>
        <table className="pl-table">
          <thead>
            <tr>
              <th>Membre</th><th>Jours présents</th><th>Temps passé</th>
              {!isTotal && <th>{def.time}</th>}
              <th>Aujourd'hui</th><th>Dernière venue</th>
            </tr>
          </thead>
          <tbody>
            {memberRows.map((m) => {
              const value = m.time[measure];
              const share = m.minutes > 0 ? Math.round((value / m.minutes) * 100) : 0;
              const todayValue = isTotal ? m.todayMinutes : m.today[measure];
              return (
                <tr key={m.userId}>
                  <td>{m.displayName}</td>
                  <td className="pl-num">{m.daysPresent} / {stats.period}</td>
                  <td className="pl-num">{m.minutes > 0 ? formatMinutes(m.minutes) : '-'}</td>
                  {!isTotal && (
                    <td className="pl-num">
                      {value > 0 ? <>{formatMinutes(value)} <span className="pl-muted">· {share} %</span></> : '-'}
                    </td>
                  )}
                  <td className="pl-num">{todayValue > 0 ? formatMinutes(todayValue) : '-'}</td>
                  <td className="pl-muted">{formatLastSeen(m.lastSeenAt)}</td>
                </tr>
              );
            })}
            {memberRows.length === 0 && (
              <tr><td colSpan={isTotal ? 5 : 6} className="pl-muted">Aucun membre ne correspond à « {search} ».</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </>
  );
}
